<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useConfig } from 'dashboard/composables/useConfig';
import StageColorPicker from './StageColorPicker.vue';
import StageIconPicker from './StageIconPicker.vue';
import LabelStageMappings from './LabelStageMappings.vue';
import draggable from 'vuedraggable';
import agents from '../../../../api/agents';
import InboxesAPI from 'dashboard/api/inboxes';
import LabelsAPI from 'dashboard/api/labels';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import ConfirmModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';
import logger from 'shared/helpers/logger';
import { areLabelStageMappingsValid } from './labelStageMappingValidation';

const props = defineProps({
  isEditing: {
    type: Boolean,
    default: false,
  },
  initialData: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['saved', 'close']);
const { t } = useI18n();
const store = useStore();
useConfig();
const currentAccountId = computed(
  () =>
    store.getters.getCurrentAccountId ??
    store.getters.getCurrentAccount?.id ??
    null
);
let accountGeneration = 0;
let saveGeneration = 0;
let resourcesRequestId = 0;
const isFormMounted = ref(false);
const normalizedAccountId = () => {
  const accountId = currentAccountId.value;
  if (accountId === undefined || accountId === null) return null;
  return String(accountId).trim() || null;
};
const openingAccountId = normalizedAccountId();
const captureAccountContext = () => ({
  accountId: openingAccountId,
  generation: accountGeneration,
});
const accountContextIsCurrent = context =>
  context.accountId !== null &&
  context.accountId === openingAccountId &&
  context.accountId === normalizedAccountId() &&
  context.generation === accountGeneration;
const loading = ref(false);
const errorMessage = ref('');
const isDirty = ref(false);
const isInitializing = ref(true);
const agentsError = ref(false);
const errorAlert = ref(null);
const discardConfirmation = ref(null);
const stageRemovalConfirmation = ref(null);
const pendingStageRemoval = ref(null);

const availableInboxes = ref([]);
const availableLabels = ref([]);
const inboxOptions = computed(() => [
  {
    value: '',
    label: t('PIPELINE.PIPELINES.FORM.INBOX_INTEGRATION.NO_INBOX'),
  },
  ...Array.from(availableInboxes.value || []).map(inbox => ({
    value: inbox.id,
    label: inbox.name,
  })),
]);

const formData = ref({
  name: props.initialData.name || '',
  description: props.initialData.description || '',
  inbox_id: props.initialData.inbox_id || '',
  stages: {},
  agents: props.initialData.agents || [],
});

// Toggle para etapas de ganho/perdido (padrão: habilitado)
const enableWonLostStages = ref(true);

// Fase 2.1 — SLA por etapa (default desabilitado por pipeline)
const stageSlaEnabled = ref(false);

// Cria um card automaticamente quando a inbox vinculada abre uma conversa nova.
// Vincular a inbox liga isto por padrão (Pipeline#ensure_entry_stage); até
// 2026-08-11 não havia como desligar — a chave nem estava na allow-list do
// controller, então o PATCH respondia 200 e descartava o valor.
const autoCreateFromInbox = ref(true);

// Auto-archive pipeline card when linked conversation is resolved (default OFF).
// When OFF the card stays on the board after the conversation is resolved.
const autoArchiveOnResolved = ref(false);

// Auto-mark card as Won when linked conversation is resolved (default OFF).
// Only effective when "Etapas Ganho/Perdido" is enabled and a Won stage exists.
const autoWinOnResolved = ref(false);
// Auditoria 2026-08-08 (G5): o backend já exige valor ao marcar um card como
// ganho quando `require_won_value` está ligado (`PipelineCard#mark_as_won!`),
// mas não havia nenhum lugar na interface para ligar isso.
const requireWonValue = ref(false);
// Auditoria 2026-08-08 (G5): `Appointments::BookingService` escolhe este funil
// quando um agendamento não traz funil explícito. A coluna existia e só o
// console alcançava — nem a API aceitava o campo.
const defaultForAppointments = ref(false);
// Valores de atalho do campo "Valor" do card. O formulário do card já lê
// `settings.quick_values`; não havia onde configurá-los.
const quickValuesInput = ref('');
// Contrato: `,`, `;` e quebra de linha SEPARAM valores; `.` é separador de
// milhar e é descartado. Então "1000, 5000" e "1.000; 5.000" dão o mesmo par.
// `Number('1.000')` em JS é 1 — o parser anterior gravaria o atalho 1 achando
// que gravou 1000, em silêncio.
//
// A vírgula é ambígua por natureza (separa e agrupa) e essa ambiguidade não tem
// solução automática: "1,000" é lido como dois valores. Por isso a prévia
// abaixo existe — ela mostra exatamente o que será gravado, antes de salvar.
// Atalho de valor é quantia inteira; não há decimal a preservar.
const parsedQuickValues = computed(() =>
  quickValuesInput.value
    .split(/[,;\n]/)
    .map(part => part.trim())
    // Só dígitos e pontos de milhar. Descartar caracteres soltos transformaria
    // "-5" em 5 e "R$ 12ab" em 12 — o valor errado, em silêncio.
    .filter(part => /^\d[\d.]*$/.test(part))
    .map(part => Number(part.replace(/\./g, '')))
    .filter(value => Number.isInteger(value) && value > 0)
);

// Label-Stage mappings state
const labelStageMappings = ref({
  label_stage_mappings: [],
  stage_label_mappings: [],
});

// Show/hide label mappings section
const showLabelMappings = ref(false);

// Label mappings validation state
const labelMappingsValid = ref(true);

const stages = ref([]);
const newStage = ref({
  name: '',
  color: '#FF6B6B',
  description: '',
  icon: 'arrow-right',
});

const editingStage = ref(null);

const dragOptions = {
  animation: 150,
  ghostClass: 'opacity-50',
};

const agentsList = ref([]);
const loadingAgents = ref(false);
const agentSearch = ref('');

// Gera o ID baseado no nome (formato temporário - backend normaliza para {pipeline_id}_{slug})
const generateId = name => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

// Atualiza o ID apenas para novas etapas
watch(
  () => newStage.value.name,
  newName => {
    if (newName && !editingStage.value) {
      newStage.value.id = generateId(newName);
    }
  }
);

// Campos que o backend aceita por etapa e o formulário não edita. Precisam
// sobreviver ao PATCH, que troca o hash de stages por inteiro.
const PRESERVED_STAGE_FIELDS = [
  'is_entry_stage',
  'wip_limit',
  'automation_actions',
];

const preservedStageFields = existingStage =>
  PRESERVED_STAGE_FIELDS.reduce((kept, field) => {
    if (existingStage[field] !== undefined) kept[field] = existingStage[field];
    return kept;
  }, {});

// Converte as etapas para o formato esperado pela API
const formatStagesForAPI = () => {
  const formattedStages = {};

  // Garantir que as posições reflitam a ordem atual
  stages.value.forEach((stage, index) => {
    // Definir explicitamente a posição baseada no índice atual
    const position = index + 1;

    // Pega os templates existentes da etapa atual
    const existingStage = props.initialData?.stages?.[stage.id] || {};
    const existingTemplates = existingStage.message_templates || [];

    formattedStages[stage.id] = {
      name: stage.name,
      color: stage.color,
      position: position, // Garantir que a posição reflita a ordem atual
      description: stage.description,
      icon: stage.icon || 'arrow-right', // Ícone do estágio (padrão: arrow-right)
      message_templates: existingTemplates, // Mantém os templates existentes
      // Auditoria 2026-08-08 (F08): o PATCH substitui o hash inteiro de stages,
      // então tudo que o formulário não reenvia é APAGADO. Estes três campos o
      // backend aceita e usa, mas a tela não edita — sem preservá-los, qualquer
      // edição visual do funil zerava a etapa de entrada (que a auto-criação por
      // inbox usa), o limite de WIP e as ações de automação da etapa.
      ...preservedStageFields(existingStage),
    };

    // Preservar flags de ganho/perdido
    if (stage.is_won_stage) {
      formattedStages[stage.id].is_won_stage = true;
    }
    if (stage.is_lost_stage) {
      formattedStages[stage.id].is_lost_stage = true;
    }

    // Fase 2.1 — SLA por etapa (só persiste se valor positivo definido)
    const slaHours = Number.parseInt(stage.sla_hours, 10);
    if (Number.isFinite(slaHours) && slaHours > 0) {
      formattedStages[stage.id].sla_hours = slaHours;
    }
  });

  return formattedStages;
};

// Função para lidar com a reordenação das etapas
const handleOrderChange = async () => {
  if (!props.isEditing) return;

  try {
    // Recalcular posições com base na nova ordem após drag-and-drop (B62)
    stages.value.forEach((stage, index) => {
      stage.position = index + 1;
    });

    // Marcar form como dirty para exibir indicador visual (B62)
    isDirty.value = true;
  } catch (error) {
    logger.error('Erro ao atualizar ordem:', error);
  }
};

// Bugfix 2026-08-11 — na criação o toggle de ganho/perdido aparecia ligado, mas
// a lista não mostrava etapa nenhuma correspondente: as terminais só nasciam no
// backend, DEPOIS do save (`Pipeline#ensure_won_lost_stages`). A tela prometia um
// recurso que não exibia. Prevê-las aqui iguala criação e edição, e o payload já
// sai com as flags — o callback do modelo vira no-op em vez de acrescentar
// etapas que o usuário nunca viu.
//
// Os nomes espelham os defaults do backend de propósito: são DADOS (nome de
// etapa, que o cliente renomeia à vontade), não rótulo de UI — traduzi-los aqui
// criaria funis com nomes diferentes conforme o idioma de quem clicou em criar.
// `fallbackKeys` espelha Pipeline::WON_STAGE_FALLBACK_KEYS / LOST_STAGE_FALLBACK_KEYS:
// são as chaves que `find_won_stage_key` já aceita como terminal sem flag nenhuma.
const TERMINAL_STAGE_DEFAULTS = [
  {
    flag: 'is_won_stage',
    fallbackKeys: ['Ganho', 'Fechado', 'Won', 'Closed'],
    name: 'Ganho',
    color: '#10b981',
    icon: 'checkmark-circle',
    description: 'Negócios fechados com sucesso',
  },
  {
    flag: 'is_lost_stage',
    fallbackKeys: ['Perdido', 'Lost'],
    name: 'Perdido',
    color: '#ef4444',
    icon: 'dismiss-circle',
    description: 'Negócios perdidos',
  },
];

const addTerminalStagePreviews = () => {
  TERMINAL_STAGE_DEFAULTS.forEach(({ flag, fallbackKeys, ...defaults }) => {
    if (stages.value.some(stage => stage[flag] === true)) return;

    // O funil pode já ter a etapa terminal sem a flag — pela chave canônica (que o
    // backend resolve no fallback) ou só pelo nome. Nos dois casos ela é MARCADA
    // no lugar, igual ao `ensure_terminal_stage` do modelo: injetar uma gêmea
    // mudaria o destino dos cards e travaria o submit por nome duplicado.
    const name = defaults.name.toLocaleLowerCase();
    const existing = stages.value.find(
      stage =>
        fallbackKeys.includes(stage.id) ||
        stage.name?.trim().toLocaleLowerCase() === name
    );
    if (existing) {
      existing[flag] = true;
      return;
    }

    stages.value.push({ id: `new_stage_${flag}`, ...defaults, [flag]: true });
  });
};

// Bugfix 2026-08-11 — enquanto o toggle de ganho/perdido está ligado, as etapas
// terminais são estruturais: é a flag `is_won_stage`/`is_lost_stage` que faz
// `find_won_stage_key` mover o card. Removê-las pela lista deixava o funil no
// estado que o cliente relatou (recurso "ativo" que não funciona). Para tirá-las
// do quadro o caminho é desligar o toggle, que as esconde sem perder os cards.
const isTerminalStage = stage =>
  Boolean(stage?.is_won_stage || stage?.is_lost_stage);

const canRemoveStage = stage =>
  !(enableWonLostStages.value && isTerminalStage(stage));

// Ganho/Perdido não são etapas de trabalho: um funil só com elas não é funil.
// Toda regra de "pelo menos uma etapa" conta só as não-terminais.
const workingStageCount = computed(
  () => stages.value.filter(stage => !isTerminalStage(stage)).length
);

const removeStage = async index => {
  const stage = stages.value[index];
  if (!canRemoveStage(stage)) return;
  if (!isTerminalStage(stage) && workingStageCount.value <= 1) return;
  const isPersistedStage = Boolean(props.initialData?.stages?.[stage.id]);
  if (isPersistedStage) {
    pendingStageRemoval.value = { id: stage.id, name: stage.name };
    const confirmed = await stageRemovalConfirmation.value.showConfirmation();
    if (!confirmed) {
      pendingStageRemoval.value = null;
      return;
    }
  }

  const currentIndex = stages.value.findIndex(item => item.id === stage.id);
  if (currentIndex !== -1) stages.value.splice(currentIndex, 1);
  pendingStageRemoval.value = null;
};

const moveStage = (index, direction) => {
  const targetIndex = index + direction;
  if (targetIndex < 0 || targetIndex >= stages.value.length) return;

  const [stage] = stages.value.splice(index, 1);
  stages.value.splice(targetIndex, 0, stage);
  handleOrderChange();
};

// Handler for label-stage mappings update
const onLabelMappingsUpdate = mappings => {
  labelStageMappings.value = mappings;
};

// Handler for label-stage mappings validation
const onLabelMappingsValidUpdate = valid => {
  labelMappingsValid.value = valid;
};

const resetAccountOwnedResources = () => {
  resourcesRequestId += 1;
  agentsList.value = [];
  availableInboxes.value = [];
  availableLabels.value = [];
  loadingAgents.value = false;
  agentsError.value = false;
  agentSearch.value = '';
};

const fetchAgents = async () => {
  const accountContext = captureAccountContext();
  if (!accountContextIsCurrent(accountContext)) {
    resetAccountOwnedResources();
    return;
  }
  resourcesRequestId += 1;
  const requestId = resourcesRequestId;
  const requestIsCurrent = () =>
    isFormMounted.value &&
    requestId === resourcesRequestId &&
    accountContextIsCurrent(accountContext);

  try {
    loadingAgents.value = true;
    agentsError.value = false;
    const [agentsResponse, inboxesResponse, labelsResponse] = await Promise.all(
      [
        agents.getForAccount(accountContext.accountId),
        InboxesAPI.getForAccount(accountContext.accountId),
        LabelsAPI.getForAccount(accountContext.accountId),
      ]
    );
    if (!requestIsCurrent()) return;
    const data = Array.isArray(agentsResponse.data)
      ? [...agentsResponse.data]
      : [];
    // Se tiver agentes pré-selecionados e eles não estiverem na lista, adiciona-os
    if (props.initialData?.agents?.length) {
      props.initialData.agents.forEach(agent => {
        if (!data.find(a => a.id === agent.id)) {
          data.push(agent);
        }
      });
    }
    agentsList.value = data;
    availableInboxes.value = Array.isArray(inboxesResponse.data?.payload)
      ? inboxesResponse.data.payload
      : [];
    availableLabels.value = Array.isArray(labelsResponse.data?.payload)
      ? [...labelsResponse.data.payload].sort((left, right) =>
          left.title.localeCompare(right.title)
        )
      : [];
  } catch (error) {
    if (!requestIsCurrent()) return;
    agentsList.value = [];
    availableInboxes.value = [];
    availableLabels.value = [];
    agentsError.value = true;
    logger.error('Erro ao carregar agentes:', error);
  } finally {
    if (requestIsCurrent()) loadingAgents.value = false;
  }
};

// Inicializa as etapas se estiver editando
onMounted(async () => {
  isFormMounted.value = true;
  if (props.isEditing || props.initialData) {
    formData.value = {
      name: props.initialData.name || '',
      description: props.initialData.description || '',
      inbox_id: props.initialData.inbox_id || '',
      stages: props.initialData.stages || {},
      agents: props.initialData.agents || [],
    };

    // Inicializa as etapas do template
    if (props.initialData.stages) {
      stages.value = Object.entries(props.initialData.stages)
        .map(([id, stage]) => {
          const mappedStage = {
            id,
            name: stage.name,
            color: stage.color,
            description: stage.description,
            icon: stage.icon || 'arrow-right',
            position: parseInt(stage.position, 10) || 0,
            // Preservar flags de ganho/perdido
            is_won_stage: stage.is_won_stage || false,
            is_lost_stage: stage.is_lost_stage || false,
            // Fase 2.1 — horas de SLA por etapa (JSONB stages[id].sla_hours)
            sla_hours:
              stage.sla_hours != null && Number(stage.sla_hours) > 0
                ? Number(stage.sla_hours)
                : null,
          };
          return mappedStage;
        })
        .sort((a, b) => a.position - b.position);
    }

    // Inicializa o toggle de ganho/perdido
    if (props.initialData?.settings?.enable_won_lost_stages !== undefined) {
      enableWonLostStages.value =
        props.initialData.settings.enable_won_lost_stages;
    } else {
      enableWonLostStages.value = true; // Padrão: habilitado
    }

    // Fase 2.1 — Inicializa toggle de SLA por etapa
    stageSlaEnabled.value = Boolean(props.initialData?.stage_sla_enabled);

    // Ausente = ligado: é o default de quem vincula uma inbox, e um funil salvo
    // antes deste toggle não deve aparecer como desligado.
    autoCreateFromInbox.value =
      props.initialData?.settings?.auto_create_from_inbox !== false;

    // Toggles opt-in para automações ao resolver a conversa
    autoArchiveOnResolved.value = Boolean(
      props.initialData?.settings?.auto_archive_on_resolved
    );
    requireWonValue.value = Boolean(
      props.initialData?.settings?.require_won_value
    );
    defaultForAppointments.value = Boolean(
      props.initialData?.default_for_appointments
    );
    quickValuesInput.value = Array.isArray(
      props.initialData?.settings?.quick_values
    )
      ? props.initialData.settings.quick_values.join(', ')
      : '';
    autoWinOnResolved.value = Boolean(
      props.initialData?.settings?.auto_win_on_resolved
    );

    // Initialize label-stage mappings
    if (props.initialData?.settings) {
      labelStageMappings.value = {
        label_stage_mappings:
          props.initialData.settings.label_stage_mappings || [],
        stage_label_mappings:
          props.initialData.settings.stage_label_mappings || [],
      };
      // Show section if there are any mappings
      showLabelMappings.value =
        labelStageMappings.value.label_stage_mappings.length > 0 ||
        labelStageMappings.value.stage_label_mappings.length > 0;
    }
  }

  // Regra única: toggle ligado ⇒ a lista mostra as etapas terminais. Na criação
  // elas ainda não existem; na edição de um funil divergente (toggle ligado, sem
  // etapa) elas aparecem em vez de o formulário repetir a promessa vazia.
  if (enableWonLostStages.value) addTerminalStagePreviews();

  fetchAgents();
  await nextTick();
  isDirty.value = false;
  isInitializing.value = false;
});

// Expanded stage for accordion
const expandedStageId = ref(null);

const handleSubmit = async () => {
  if (loading.value) return;
  const accountContext = captureAccountContext();
  if (!accountContextIsCurrent(accountContext)) return;
  saveGeneration += 1;
  const requestGeneration = saveGeneration;
  const requestIsCurrent = () =>
    requestGeneration === saveGeneration &&
    accountContextIsCurrent(accountContext);

  try {
    loading.value = true;
    errorMessage.value = '';

    if (!formData.value.name.trim()) {
      errorMessage.value = t('PIPELINE.PIPELINES.FORM.NAME.REQUIRED');
      await nextTick();
      errorAlert.value?.focus();
      return;
    }

    if (workingStageCount.value === 0) {
      errorMessage.value = t('PIPELINE.ERRORS.STAGE_REQUIRED');
      await nextTick();
      errorAlert.value?.focus();
      return;
    }

    // Validate stage names before sending to API
    const emptyStage = stages.value.find(s => !s.name?.trim());
    if (emptyStage) {
      expandedStageId.value = emptyStage.id;
      errorMessage.value = t('PIPELINE.PIPELINES.FORM.STAGES.NAME_REQUIRED');
      await nextTick();
      errorAlert.value?.focus();
      return;
    }

    const normalizedStageNames = stages.value.map(stage =>
      stage.name.trim().toLocaleLowerCase()
    );
    if (new Set(normalizedStageNames).size !== normalizedStageNames.length) {
      errorMessage.value = t('PIPELINE.PIPELINES.FORM.STAGES.DUPLICATE_NAME');
      await nextTick();
      errorAlert.value?.focus();
      return;
    }

    labelMappingsValid.value = areLabelStageMappingsValid({
      mappings: labelStageMappings.value,
      stages: stages.value,
      labels: availableLabels.value,
    });
    if (!labelMappingsValid.value) {
      showLabelMappings.value = true;
      errorMessage.value = t('PIPELINE.PIPELINES.FORM.MAPPINGS_INVALID');
      await nextTick();
      errorAlert.value?.focus();
      return;
    }

    // Preparar as etapas mantendo os templates existentes e atualizando posições
    const formattedStages = formatStagesForAPI();

    const payload = {
      name: formData.value.name.trim(),
      description: formData.value.description.trim(),
      inbox_id: formData.value.inbox_id || null,
      // Preencher horas de SLA em qualquer etapa já liga o recurso. Antes o
      // toggle era pré-condição independente: quem configurava as horas sem
      // ligá-lo ficava com a SLA inerte e sem aviso (relato de cliente
      // 2026-08-05; em produção a flag estava false em 100% das pipelines).
      stage_sla_enabled:
        stageSlaEnabled.value ||
        Object.values(formattedStages).some(
          stage => Number(stage?.sla_hours) > 0
        ),
      stages: formattedStages,
      agents: formData.value.agents,
      default_for_appointments: defaultForAppointments.value,
      settings: {
        ...(props.initialData?.settings || {}),
        enable_won_lost_stages: enableWonLostStages.value,
        label_stage_mappings: labelStageMappings.value.label_stage_mappings,
        stage_label_mappings: labelStageMappings.value.stage_label_mappings,
        require_won_value: enableWonLostStages.value && requireWonValue.value,
        quick_values: parsedQuickValues.value,
        auto_archive_on_resolved: autoArchiveOnResolved.value,
        auto_win_on_resolved:
          enableWonLostStages.value && autoWinOnResolved.value,
        // Sem inbox o toggle nem aparece; gravar `false` aí registraria uma
        // escolha que o admin não fez e bloquearia o default ao vincular depois.
        ...(formData.value.inbox_id
          ? { auto_create_from_inbox: autoCreateFromInbox.value }
          : {}),
      },
    };

    let savedPipeline;
    if (props.isEditing) {
      savedPipeline = await store.dispatch('pipelines/updatePipeline', {
        id: props.initialData.id,
        accountId: accountContext.accountId,
        ...payload,
      });
    } else {
      savedPipeline = await store.dispatch('pipelines/createPipeline', {
        accountId: accountContext.accountId,
        ...payload,
      });
    }

    if (requestIsCurrent() && savedPipeline) {
      isDirty.value = false;
      emit('saved', savedPipeline);
    }
  } catch (error) {
    if (!requestIsCurrent()) return;
    logger.error('Erro ao salvar funil:', error);
    if (error.response?.data?.code === 'PIPELINE_LIMIT_REACHED') {
      errorMessage.value = error.response.data.error;
    } else if (error.response?.data?.errors) {
      // Parse Rails validation errors from the API response
      const apiErrors = error.response.data.errors;
      const messages = Object.entries(apiErrors)
        .flatMap(([field, msgs]) =>
          (Array.isArray(msgs) ? msgs : [msgs]).map(
            msg => `${field === 'base' ? '' : `${field}: `}${msg}`
          )
        )
        .join('; ');
      errorMessage.value =
        messages || t('PIPELINE.PIPELINES.FORM.ERROR_GENERIC');
    } else {
      errorMessage.value =
        error.response?.data?.error ||
        error.message ||
        t('PIPELINE.PIPELINES.FORM.ERROR_GENERIC');
    }
    await nextTick();
    errorAlert.value?.focus();
  } finally {
    if (requestIsCurrent()) loading.value = false;
  }
};

watch(
  currentAccountId,
  (accountId, previousAccountId) => {
    if (String(accountId ?? '') === String(previousAccountId ?? '')) return;
    accountGeneration += 1;
    saveGeneration += 1;
    loading.value = false;
    errorMessage.value = '';
    resetAccountOwnedResources();
    formData.value = {
      name: '',
      description: '',
      inbox_id: '',
      stages: {},
      agents: [],
    };
    stages.value = [];
    editingStage.value = null;
    pendingStageRemoval.value = null;
    expandedStageId.value = null;
    labelStageMappings.value = {
      label_stage_mappings: [],
      stage_label_mappings: [],
    };
    enableWonLostStages.value = true;
    stageSlaEnabled.value = false;
    autoCreateFromInbox.value = true;
    autoArchiveOnResolved.value = false;
    autoWinOnResolved.value = false;
    showLabelMappings.value = false;
    labelMappingsValid.value = true;
    emit('close');
  },
  { flush: 'sync' }
);

const requestClose = async () => {
  if (loading.value) return false;
  if (!isDirty.value) return true;

  const confirmed = await discardConfirmation.value.showConfirmation();
  return confirmed;
};

const handleClose = async () => {
  if (await requestClose()) {
    resetAccountOwnedResources();
    emit('close');
  }
};

defineExpose({ requestClose });

const toggleStageExpand = stageId => {
  if (expandedStageId.value === stageId) {
    expandedStageId.value = null;
  } else {
    expandedStageId.value = stageId;
  }
};

// Search agents
const filteredAgents = computed(() => {
  if (!agentSearch.value) return agentsList.value;
  const search = agentSearch.value.toLowerCase();
  return agentsList.value.filter(agent =>
    agent.name.toLowerCase().includes(search)
  );
});

// Add new stage (for accordion style)
// Gera ID temporário - o backend normaliza para {pipeline_id}_{slug} após salvar
const addNewStage = () => {
  const tempId = `new_stage_${Date.now()}`;
  stages.value.push({
    id: tempId,
    name: '',
    color: '#6366f1',
    description: '',
    icon: 'arrow-right',
  });
  expandedStageId.value = tempId;
};

// Remove agent from selected
const removeAgent = agentId => {
  const index = formData.value.agents.findIndex(a => a.id === agentId);
  if (index !== -1) {
    formData.value.agents.splice(index, 1);
  }
};

const toggleAgent = agent => {
  const index = formData.value.agents.findIndex(a => a.id === agent.id);
  if (index === -1) {
    formData.value.agents.push(agent);
  } else {
    formData.value.agents.splice(index, 1);
  }
};

watch(enableWonLostStages, enabled => {
  if (!enabled) {
    autoWinOnResolved.value = false;
    return;
  }
  // Ligar o toggle já mostra as etapas que o save vai garantir.
  addTerminalStagePreviews();
});

watch(
  [
    formData,
    stages,
    enableWonLostStages,
    stageSlaEnabled,
    autoCreateFromInbox,
    autoArchiveOnResolved,
    autoWinOnResolved,
    labelStageMappings,
  ],
  () => {
    if (!isInitializing.value) isDirty.value = true;
  },
  { deep: true }
);

onBeforeUnmount(() => {
  isFormMounted.value = false;
  resetAccountOwnedResources();
});
</script>

<template>
  <form novalidate @submit.prevent="handleSubmit">
    <!-- Mensagem de erro -->
    <div
      v-if="errorMessage"
      ref="errorAlert"
      role="alert"
      tabindex="-1"
      class="mb-4 p-3 bg-n-ruby-3 border border-n-ruby-9 rounded-lg"
    >
      <div class="flex items-center gap-2">
        <fluent-icon icon="error-circle" size="16" class="text-n-ruby-11" />
        <span class="text-sm text-n-ruby-11">{{ errorMessage }}</span>
      </div>
    </div>

    <!-- Main Grid: 2 + 3 columns -->
    <div class="grid grid-cols-1 md:grid-cols-5 gap-6">
      <!-- Coluna Esquerda (2/5) -->
      <div class="md:col-span-2 space-y-4">
        <!-- Card: Nome, Descrição e Inbox -->
        <div
          class="bg-n-solid-2 p-4 rounded-xl outline outline-1 outline-n-weak"
        >
          <div class="space-y-4">
            <div>
              <label
                for="pipeline-name"
                class="block text-label text-n-slate-12 mb-1"
              >
                {{ t('PIPELINE.PIPELINES.FORM.NAME.LABEL') }}
              </label>
              <input
                id="pipeline-name"
                v-model="formData.name"
                name="pipelineName"
                type="text"
                class="min-h-11 w-full"
                :placeholder="t('PIPELINE.PIPELINES.FORM.NAME.PLACEHOLDER')"
                aria-required="true"
              />
            </div>
            <div>
              <label
                for="pipeline-description"
                class="block text-label text-n-slate-12 mb-1"
              >
                {{ t('PIPELINE.PIPELINES.FORM.DESCRIPTION.LABEL') }}
              </label>
              <textarea
                id="pipeline-description"
                v-model="formData.description"
                name="pipelineDescription"
                rows="3"
                class="w-full px-3 py-2 border border-n-weak rounded-lg text-sm bg-n-solid-2 text-n-slate-12 resize-none focus:border-n-brand focus:ring-1 focus:ring-n-brand/20"
                :placeholder="
                  t('PIPELINE.PIPELINES.FORM.DESCRIPTION.PLACEHOLDER')
                "
              />
            </div>
            <div>
              <label
                for="pipeline-inbox"
                class="block text-label text-n-slate-12 mb-1"
              >
                {{
                  t('PIPELINE.PIPELINES.FORM.INBOX_INTEGRATION.SELECT_INBOX')
                }}
                <span class="text-n-slate-10 font-normal text-xs">
                  {{ t('PIPELINE.PIPELINES.FORM.INBOX_INTEGRATION.OPTIONAL') }}
                </span>
              </label>
              <Select
                id="pipeline-inbox-select"
                v-model="formData.inbox_id"
                input-id="pipeline-inbox"
                name="pipelineInbox"
                class="w-full [&>select]:min-h-11 [&>select]:w-full"
                :aria-label="
                  t('PIPELINE.PIPELINES.FORM.INBOX_INTEGRATION.SELECT_INBOX')
                "
                :options="inboxOptions"
              />
              <p class="mt-1.5 text-xs text-n-slate-11">
                {{ t('PIPELINE.PIPELINES.FORM.INBOX_INTEGRATION.DESCRIPTION') }}
              </p>
              <div
                v-if="formData.inbox_id"
                class="mt-2 p-2.5 bg-n-blue-3 border border-n-blue-9 rounded-lg"
              >
                <div class="flex items-start gap-2">
                  <fluent-icon
                    icon="info"
                    size="14"
                    class="text-n-blue-11 mt-0.5 flex-shrink-0"
                  />
                  <span class="text-xs text-n-blue-11">
                    {{
                      t(
                        'PIPELINE.PIPELINES.FORM.INBOX_INTEGRATION.AUTO_CREATE_INFO'
                      )
                    }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Toggle: Etapas Ganho/Perdido -->
            <div class="p-3 bg-n-alpha-1 border border-n-weak rounded-lg">
              <label
                class="flex min-h-11 items-center justify-between cursor-pointer"
              >
                <div>
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ t('PIPELINE.PIPELINES.FORM.WON_LOST_STAGES.LABEL') }}
                  </span>
                  <p class="text-xs text-n-slate-11 mt-0.5">
                    {{
                      t('PIPELINE.PIPELINES.FORM.WON_LOST_STAGES.DESCRIPTION')
                    }}
                  </p>
                </div>
                <div class="relative ml-3 flex-shrink-0">
                  <input
                    v-model="enableWonLostStages"
                    type="checkbox"
                    class="sr-only peer"
                  />
                  <div
                    class="w-11 h-6 bg-n-alpha-2 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-n-weak after:border after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-n-brand peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-n-brand"
                  />
                </div>
              </label>
            </div>

            <!-- Toggle: exigir valor ao ganhar (auditoria G5) -->
            <div
              v-if="enableWonLostStages"
              class="p-3 bg-n-alpha-1 border border-n-weak rounded-lg"
            >
              <label class="flex items-start cursor-pointer !mb-0">
                <div class="flex-1">
                  <p class="text-sm font-medium text-n-slate-12">
                    {{ t('PIPELINE.PIPELINES.FORM.REQUIRE_WON_VALUE.LABEL') }}
                  </p>
                  <p class="text-xs text-n-slate-11 mt-0.5">
                    {{
                      t('PIPELINE.PIPELINES.FORM.REQUIRE_WON_VALUE.DESCRIPTION')
                    }}
                  </p>
                </div>
                <div class="relative ml-3 flex-shrink-0">
                  <input
                    v-model="requireWonValue"
                    data-testid="require-won-value-toggle"
                    type="checkbox"
                    class="sr-only peer"
                  />
                  <div
                    class="w-11 h-6 bg-n-alpha-2 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-n-weak after:border after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-n-brand peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-n-brand"
                  />
                </div>
              </label>
            </div>

            <!-- Toggle: funil padrão de agendamentos (auditoria G5) -->
            <div class="p-3 bg-n-alpha-1 border border-n-weak rounded-lg">
              <label class="flex items-start cursor-pointer !mb-0">
                <div class="flex-1">
                  <p class="text-sm font-medium text-n-slate-12">
                    {{
                      t(
                        'PIPELINE.PIPELINES.FORM.DEFAULT_FOR_APPOINTMENTS.LABEL'
                      )
                    }}
                  </p>
                  <p class="text-xs text-n-slate-11 mt-0.5">
                    {{
                      t(
                        'PIPELINE.PIPELINES.FORM.DEFAULT_FOR_APPOINTMENTS.DESCRIPTION'
                      )
                    }}
                  </p>
                </div>
                <div class="relative ml-3 flex-shrink-0">
                  <input
                    v-model="defaultForAppointments"
                    data-testid="default-for-appointments-toggle"
                    type="checkbox"
                    class="sr-only peer"
                  />
                  <div
                    class="w-11 h-6 bg-n-alpha-2 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-n-weak after:border after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-n-brand peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-n-brand"
                  />
                </div>
              </label>
            </div>

            <!-- Valores de atalho do card (auditoria G5) -->
            <div class="p-3 bg-n-alpha-1 border border-n-weak rounded-lg">
              <label class="block !mb-0">
                <p class="text-sm font-medium text-n-slate-12">
                  {{ t('PIPELINE.PIPELINES.FORM.QUICK_VALUES.LABEL') }}
                </p>
                <p class="text-xs text-n-slate-11 mt-0.5 mb-2">
                  {{ t('PIPELINE.PIPELINES.FORM.QUICK_VALUES.DESCRIPTION') }}
                </p>
                <input
                  v-model="quickValuesInput"
                  data-testid="quick-values-input"
                  type="text"
                  inputmode="numeric"
                  class="w-full !mb-0 text-sm"
                  :placeholder="
                    t('PIPELINE.PIPELINES.FORM.QUICK_VALUES.PLACEHOLDER')
                  "
                />
              </label>
              <p
                v-if="parsedQuickValues.length"
                class="text-xs text-n-slate-11 mt-2"
              >
                {{
                  t('PIPELINE.PIPELINES.FORM.QUICK_VALUES.PREVIEW', {
                    values: parsedQuickValues.join(' · '),
                  })
                }}
              </p>
            </div>

            <!-- Toggle: SLA por etapa (Fase 2.1) -->
            <div class="p-3 bg-n-alpha-1 border border-n-weak rounded-lg">
              <label
                class="flex min-h-11 items-center justify-between cursor-pointer"
              >
                <div>
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ t('PIPELINE.SLA.SETTINGS_ENABLE') }}
                  </span>
                  <p class="text-xs text-n-slate-11 mt-0.5">
                    {{ t('PIPELINE.SLA.SETTINGS_HELP') }}
                  </p>
                </div>
                <div class="relative ml-3 flex-shrink-0">
                  <input
                    v-model="stageSlaEnabled"
                    type="checkbox"
                    class="sr-only peer"
                  />
                  <div
                    class="w-11 h-6 bg-n-alpha-2 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-n-weak after:border after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-n-brand peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-n-brand"
                  />
                </div>
              </label>
            </div>

            <!-- Toggle: criar card automaticamente para conversa nova da inbox.
                 Só faz sentido com inbox vinculada — é ela que dispara o
                 `conversation_created` lido por AutoCreateFromConversationService. -->
            <div
              v-if="formData.inbox_id"
              class="p-3 bg-n-alpha-1 border border-n-weak rounded-lg"
            >
              <label
                class="flex min-h-11 items-center justify-between cursor-pointer"
              >
                <div>
                  <span class="text-sm font-medium text-n-slate-12">
                    {{
                      t('PIPELINE.PIPELINES.FORM.AUTO_CREATE_FROM_INBOX.LABEL')
                    }}
                  </span>
                  <p class="text-xs text-n-slate-11 mt-0.5">
                    {{
                      t(
                        'PIPELINE.PIPELINES.FORM.AUTO_CREATE_FROM_INBOX.DESCRIPTION'
                      )
                    }}
                  </p>
                </div>
                <div class="relative ml-3 flex-shrink-0">
                  <input
                    v-model="autoCreateFromInbox"
                    type="checkbox"
                    class="sr-only peer"
                  />
                  <div
                    class="w-11 h-6 bg-n-alpha-2 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-n-weak after:border after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-n-brand peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-n-brand"
                  />
                </div>
              </label>
            </div>

            <!-- Toggle: Arquivar card ao resolver a conversa -->
            <div class="p-3 bg-n-alpha-1 border border-n-weak rounded-lg">
              <label
                class="flex min-h-11 items-center justify-between cursor-pointer"
              >
                <div>
                  <span class="text-sm font-medium text-n-slate-12">
                    {{
                      t(
                        'PIPELINE.PIPELINES.FORM.AUTO_ARCHIVE_ON_RESOLVED.LABEL'
                      )
                    }}
                  </span>
                  <p class="text-xs text-n-slate-11 mt-0.5">
                    {{
                      t(
                        'PIPELINE.PIPELINES.FORM.AUTO_ARCHIVE_ON_RESOLVED.DESCRIPTION'
                      )
                    }}
                  </p>
                </div>
                <div class="relative ml-3 flex-shrink-0">
                  <input
                    v-model="autoArchiveOnResolved"
                    type="checkbox"
                    class="sr-only peer"
                  />
                  <div
                    class="w-11 h-6 bg-n-alpha-2 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-n-weak after:border after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-n-brand peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-n-brand"
                  />
                </div>
              </label>
            </div>

            <!-- Toggle: Marcar como Ganho ao resolver a conversa (requer Ganho/Perdido) -->
            <div
              v-if="enableWonLostStages"
              class="p-3 bg-n-alpha-1 border border-n-weak rounded-lg"
            >
              <label
                class="flex min-h-11 items-center justify-between cursor-pointer"
              >
                <div>
                  <span class="text-sm font-medium text-n-slate-12">
                    {{
                      t('PIPELINE.PIPELINES.FORM.AUTO_WIN_ON_RESOLVED.LABEL')
                    }}
                  </span>
                  <p class="text-xs text-n-slate-11 mt-0.5">
                    {{
                      t(
                        'PIPELINE.PIPELINES.FORM.AUTO_WIN_ON_RESOLVED.DESCRIPTION'
                      )
                    }}
                  </p>
                </div>
                <div class="relative ml-3 flex-shrink-0">
                  <input
                    v-model="autoWinOnResolved"
                    type="checkbox"
                    class="sr-only peer"
                  />
                  <div
                    class="w-11 h-6 bg-n-alpha-2 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-n-weak after:border after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-n-brand peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-n-brand"
                  />
                </div>
              </label>
            </div>
          </div>
        </div>

        <!-- Card: Agentes do Funil -->
        <div
          class="bg-n-solid-2 p-4 rounded-xl outline outline-1 outline-n-weak"
        >
          <h2 class="text-heading-3 text-n-slate-12 mb-4">
            {{ t('PIPELINE.PIPELINES.FORM.AGENTS.TITLE') }}
          </h2>

          <!-- Search -->
          <div class="relative mb-4">
            <fluent-icon
              icon="search"
              size="16"
              class="absolute left-3 top-1/2 -translate-y-1/2 text-n-slate-10"
            />
            <input
              id="pipeline-agent-search"
              v-model="agentSearch"
              name="pipelineAgentSearch"
              type="text"
              class="min-h-11 w-full pl-10 pr-4 py-2 border border-n-weak rounded-lg text-sm bg-n-solid-2 text-n-slate-12 focus:border-n-brand focus:ring-1 focus:ring-n-brand/20"
              :aria-label="
                t('PIPELINE.PIPELINES.FORM.AGENTS.SEARCH_PLACEHOLDER')
              "
              :placeholder="
                t('PIPELINE.PIPELINES.FORM.AGENTS.SEARCH_PLACEHOLDER')
              "
            />
          </div>

          <!-- Loading State -->
          <div
            v-if="loadingAgents"
            role="status"
            class="flex items-center justify-center py-6"
          >
            <span
              class="w-6 h-6 border-2 border-n-weak border-t-n-brand rounded-full animate-spin"
            />
            <span class="sr-only">{{ t('PIPELINE.LOADING') }}</span>
          </div>

          <div
            v-else-if="agentsError"
            role="alert"
            class="flex flex-col items-center gap-3 rounded-lg border border-n-ruby-8 bg-n-ruby-3 p-4 text-center"
          >
            <p class="text-sm text-n-ruby-11">
              {{ t('PIPELINE.PIPELINES.FORM.AGENTS.LOAD_ERROR') }}
            </p>
            <Button
              type="button"
              variant="outline"
              color="ruby"
              size="md"
              @click="fetchAgents"
            >
              {{ t('PIPELINE.PIPELINES.FORM.AGENTS.RETRY') }}
            </Button>
          </div>

          <!-- Selected Agents List -->
          <div v-else-if="formData.agents.length" class="space-y-2 mb-4">
            <div
              v-for="agent in formData.agents"
              :key="agent.id"
              class="flex items-center gap-3 p-2 bg-n-alpha-1 rounded-lg"
            >
              <Avatar
                :name="agent.name"
                :src="agent.avatar_url"
                :size="32"
                :status="agent.availability_status"
              />
              <span class="flex-grow font-medium text-sm text-n-slate-12">
                {{ agent.name }}
              </span>
              <button
                type="button"
                class="flex size-11 items-center justify-center rounded-lg text-n-ruby-11 hover:bg-n-ruby-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-ruby-9"
                :aria-label="
                  t('PIPELINE.PIPELINES.FORM.AGENTS.REMOVE', {
                    name: agent.name,
                  })
                "
                @click="removeAgent(agent.id)"
              >
                <fluent-icon icon="delete" size="16" />
              </button>
            </div>
          </div>

          <!-- Available Agents to Add -->
          <div
            v-if="filteredAgents.length && !loadingAgents"
            class="space-y-2 max-h-[200px] overflow-y-auto"
          >
            <button
              v-for="agent in filteredAgents.filter(
                a => !formData.agents.some(s => s.id === a.id)
              )"
              :key="agent.id"
              type="button"
              class="flex min-h-11 w-full items-center gap-3 p-2 hover:bg-n-alpha-1 rounded-lg cursor-pointer transition-colors text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
              :aria-label="
                t('PIPELINE.PIPELINES.FORM.AGENTS.ADD', { name: agent.name })
              "
              @click="toggleAgent(agent)"
            >
              <Avatar
                :name="agent.name"
                :src="agent.avatar_url"
                :size="32"
                :status="agent.availability_status"
              />
              <span class="flex-grow font-medium text-sm text-n-slate-12">
                {{ agent.name }}
              </span>
              <fluent-icon icon="add" size="16" class="text-n-brand" />
            </button>
          </div>

          <!-- Empty State -->
          <div
            v-if="!loadingAgents && !agentsError && !agentsList.length"
            class="flex items-center justify-center py-6 text-n-slate-10"
          >
            <fluent-icon icon="people" size="20" class="mr-2" />
            <span class="text-sm">{{
              t('PIPELINE.PIPELINES.FORM.AGENTS.EMPTY')
            }}</span>
          </div>
        </div>

        <!-- Card: Automações de Labels -->
        <div
          class="bg-n-solid-2 p-4 rounded-xl outline outline-1 outline-n-weak"
        >
          <button
            type="button"
            class="w-full min-h-11 flex items-center justify-between rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
            :aria-expanded="showLabelMappings"
            @click="showLabelMappings = !showLabelMappings"
          >
            <div class="flex items-center gap-2">
              <fluent-icon icon="tag" size="18" class="text-n-brand" />
              <div class="text-left">
                <h2 class="text-heading-3 text-n-slate-12">
                  {{ t('PIPELINE.LABEL_MAPPINGS.TITLE') }}
                </h2>
                <p class="text-xs text-n-slate-11">
                  {{ t('PIPELINE.LABEL_MAPPINGS.SUBTITLE') }}
                </p>
              </div>
            </div>
            <fluent-icon
              :icon="showLabelMappings ? 'chevron-up' : 'chevron-down'"
              size="16"
              class="text-n-slate-10"
            />
          </button>

          <!-- Expandable content -->
          <div
            v-if="showLabelMappings"
            class="mt-4 pt-4 border-t border-n-weak"
          >
            <LabelStageMappings
              :pipeline="initialData"
              :stages="stages"
              @update:mappings="onLabelMappingsUpdate"
              @update:valid="onLabelMappingsValidUpdate"
            />
          </div>
        </div>
      </div>

      <!-- Coluna Direita (3/5) - Etapas -->
      <div
        class="md:col-span-3 bg-n-solid-2 p-4 rounded-xl outline outline-1 outline-n-weak"
      >
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-heading-3 text-n-slate-12">
            {{ t('PIPELINE.PIPELINES.FORM.STAGES.TITLE') }}
          </h2>
          <!-- Badge "Alterações não salvas" (B62) — aparece após drag-and-drop -->
          <span
            v-if="isDirty"
            class="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-n-amber-3 text-n-amber-11 border border-n-amber-6"
          >
            {{ t('PIPELINE.PIPELINES.FORM.STAGES.UNSAVED_CHANGES') }}
          </span>
        </div>

        <!-- Lista de Etapas (Accordion) -->
        <div class="space-y-2">
          <draggable
            v-model="stages"
            :options="dragOptions"
            item-key="id"
            handle=".drag-handle"
            class="space-y-2"
            @change="handleOrderChange"
          >
            <template #item="{ element: stage, index }">
              <div
                v-if="
                  enableWonLostStages ||
                  (!stage.is_won_stage && !stage.is_lost_stage)
                "
                class="border rounded-lg bg-n-solid-2"
                :class="{
                  'border-2 border-n-brand': expandedStageId === stage.id,
                  'border-n-weak': expandedStageId !== stage.id,
                }"
              >
                <!-- Stage Header (collapsed) -->
                <div
                  class="flex min-h-12 items-center gap-1 p-2 sm:gap-2"
                  :class="{
                    'border-b border-n-weak': expandedStageId === stage.id,
                  }"
                >
                  <button
                    type="button"
                    class="drag-handle flex size-11 shrink-0 cursor-grab items-center justify-center rounded-lg text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand active:cursor-grabbing"
                    :aria-label="
                      t('PIPELINE.PIPELINES.FORM.STAGES.DRAG', {
                        name:
                          stage.name ||
                          t('PIPELINE.PIPELINES.FORM.STAGES.NEW_STAGE'),
                      })
                    "
                  >
                    <fluent-icon icon="navigation" size="16" />
                  </button>
                  <button
                    type="button"
                    class="flex min-h-11 min-w-0 flex-1 items-center gap-2 rounded-lg px-2 text-left hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                    :aria-expanded="expandedStageId === stage.id"
                    @click="toggleStageExpand(stage.id)"
                  >
                    <fluent-icon
                      :icon="stage.icon || 'arrow-right'"
                      size="16"
                      class="shrink-0 text-n-brand"
                    />
                    <span
                      class="flex-grow truncate text-sm font-medium text-n-slate-12"
                    >
                      {{
                        stage.name ||
                        t('PIPELINE.PIPELINES.FORM.STAGES.NEW_STAGE')
                      }}
                    </span>
                    <fluent-icon
                      :icon="
                        expandedStageId === stage.id
                          ? 'chevron-up'
                          : 'chevron-down'
                      "
                      size="16"
                      class="shrink-0 text-n-slate-10"
                    />
                  </button>
                  <div class="flex shrink-0 items-center">
                    <button
                      type="button"
                      class="flex size-11 items-center justify-center rounded-lg text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand disabled:cursor-not-allowed disabled:opacity-40"
                      :disabled="index === 0"
                      :aria-label="
                        t('PIPELINE.PIPELINES.FORM.STAGES.MOVE_UP', {
                          name:
                            stage.name ||
                            t('PIPELINE.PIPELINES.FORM.STAGES.NEW_STAGE'),
                        })
                      "
                      @click="moveStage(index, -1)"
                    >
                      <fluent-icon icon="chevron-up" size="16" />
                    </button>
                    <button
                      type="button"
                      class="flex size-11 items-center justify-center rounded-lg text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand disabled:cursor-not-allowed disabled:opacity-40"
                      :disabled="index === stages.length - 1"
                      :aria-label="
                        t('PIPELINE.PIPELINES.FORM.STAGES.MOVE_DOWN', {
                          name:
                            stage.name ||
                            t('PIPELINE.PIPELINES.FORM.STAGES.NEW_STAGE'),
                        })
                      "
                      @click="moveStage(index, 1)"
                    >
                      <fluent-icon icon="chevron-down" size="16" />
                    </button>
                  </div>
                </div>

                <!-- Stage Form (expanded) -->
                <div v-if="expandedStageId === stage.id" class="p-4 space-y-4">
                  <div>
                    <label
                      :for="`pipeline-stage-name-${stage.id}`"
                      class="block text-label text-n-slate-12 mb-1"
                    >
                      {{ t('PIPELINE.PIPELINES.FORM.STAGES.NAME') }}
                    </label>
                    <input
                      :id="`pipeline-stage-name-${stage.id}`"
                      v-model="stage.name"
                      :name="`pipelineStageName-${stage.id}`"
                      type="text"
                      class="min-h-11 w-full"
                      :placeholder="
                        t('PIPELINE.PIPELINES.FORM.STAGES.NAME_PLACEHOLDER')
                      "
                    />
                  </div>
                  <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <span class="block text-label text-n-slate-12 mb-1">
                        {{ t('PIPELINE.PIPELINES.FORM.STAGES.COLOR') }}
                      </span>
                      <StageColorPicker
                        v-model="stage.color"
                        :aria-label="
                          t('PIPELINE.PIPELINES.FORM.STAGES.COLOR_FOR', {
                            name:
                              stage.name ||
                              t('PIPELINE.PIPELINES.FORM.STAGES.NEW_STAGE'),
                          })
                        "
                      />
                    </div>
                    <div>
                      <label
                        :for="`pipeline-stage-icon-${stage.id}`"
                        class="block text-label text-n-slate-12 mb-1"
                      >
                        {{ t('PIPELINE.PIPELINES.FORM.STAGES.ICON') }}
                      </label>
                      <StageIconPicker
                        v-model="stage.icon"
                        :input-id="`pipeline-stage-icon-${stage.id}`"
                        :name="`pipelineStageIcon-${stage.id}`"
                        :aria-label="
                          t('PIPELINE.PIPELINES.FORM.STAGES.ICON_FOR', {
                            name:
                              stage.name ||
                              t('PIPELINE.PIPELINES.FORM.STAGES.NEW_STAGE'),
                          })
                        "
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      :for="`pipeline-stage-description-${stage.id}`"
                      class="block text-label text-n-slate-12 mb-1"
                    >
                      {{ t('PIPELINE.PIPELINES.FORM.STAGES.DESCRIPTION') }}
                    </label>
                    <input
                      :id="`pipeline-stage-description-${stage.id}`"
                      v-model="stage.description"
                      :name="`pipelineStageDescription-${stage.id}`"
                      type="text"
                      class="min-h-11 w-full"
                      :placeholder="
                        t(
                          'PIPELINE.PIPELINES.FORM.STAGES.DESCRIPTION_PLACEHOLDER'
                        )
                      "
                    />
                  </div>
                  <div
                    v-if="
                      stageSlaEnabled &&
                      !stage.is_won_stage &&
                      !stage.is_lost_stage
                    "
                  >
                    <label
                      :for="`pipeline-stage-sla-${stage.id}`"
                      class="block text-label text-n-slate-12 mb-1"
                    >
                      {{ t('PIPELINE.SLA.STAGE_HOURS_LABEL') }}
                    </label>
                    <input
                      :id="`pipeline-stage-sla-${stage.id}`"
                      v-model.number="stage.sla_hours"
                      :name="`pipelineStageSla-${stage.id}`"
                      type="number"
                      min="0"
                      step="1"
                      class="min-h-11 w-full"
                      :placeholder="t('PIPELINE.SLA.STAGE_HOURS_PLACEHOLDER')"
                    />
                    <p class="mt-1.5 text-xs text-n-slate-11">
                      {{ t('PIPELINE.SLA.STAGE_HOURS_HELP') }}
                    </p>
                  </div>
                  <div class="flex justify-end">
                    <p
                      v-if="!canRemoveStage(stage)"
                      class="text-xs text-n-slate-11"
                    >
                      {{ t('PIPELINE.PIPELINES.FORM.STAGES.TERMINAL_LOCKED') }}
                    </p>
                    <button
                      v-else
                      type="button"
                      class="flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm font-medium text-n-ruby-11 hover:bg-n-ruby-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-ruby-9 disabled:cursor-not-allowed disabled:opacity-50"
                      :disabled="workingStageCount <= 1"
                      @click="removeStage(index)"
                    >
                      <fluent-icon icon="delete" size="14" />
                      {{ t('PIPELINE.PIPELINES.FORM.STAGES.REMOVE') }}
                    </button>
                  </div>
                </div>
              </div>
            </template>
          </draggable>

          <!-- Botão Adicionar Etapa -->
          <button
            type="button"
            class="w-full min-h-11 flex items-center justify-center gap-2 py-3 border-2 border-dashed border-n-weak rounded-lg text-sm font-medium text-n-brand hover:bg-n-alpha-1 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
            @click="addNewStage"
          >
            <fluent-icon icon="add" size="16" />
            {{ t('PIPELINE.PIPELINES.FORM.STAGES.ADD') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div
      class="mt-6 flex flex-col-reverse items-stretch justify-end gap-3 border-t border-n-weak pt-4 sm:flex-row sm:items-center"
    >
      <Button
        type="button"
        variant="ghost"
        color="slate"
        size="md"
        :disabled="loading"
        class="w-full sm:w-auto"
        @click="handleClose"
      >
        {{ t('PIPELINE.CANCEL') }}
      </Button>
      <Button
        variant="solid"
        color="blue"
        size="md"
        type="submit"
        class="w-full sm:w-auto"
        :is-loading="loading"
        :disabled="loading"
      >
        <template v-if="isEditing">{{ t('PIPELINE.SAVE') }}</template>
        <template v-else>
          {{ t('PIPELINE.PIPELINES.FORM.CREATE.TITLE') }}
        </template>
      </Button>
    </div>
  </form>

  <ConfirmModal
    ref="discardConfirmation"
    :title="t('PIPELINE.PIPELINES.FORM.DISCARD.TITLE')"
    :description="t('PIPELINE.PIPELINES.FORM.DISCARD.DESCRIPTION')"
    :confirm-label="t('PIPELINE.PIPELINES.FORM.DISCARD.CONFIRM')"
    :cancel-label="t('PIPELINE.PIPELINES.FORM.DISCARD.KEEP_EDITING')"
  />

  <ConfirmModal
    ref="stageRemovalConfirmation"
    :title="t('PIPELINE.PIPELINES.FORM.STAGES.REMOVE_TITLE')"
    :description="
      t('PIPELINE.PIPELINES.FORM.STAGES.REMOVE_DESCRIPTION', {
        name: pendingStageRemoval?.name,
      })
    "
    :confirm-label="t('PIPELINE.PIPELINES.FORM.STAGES.REMOVE_CONFIRM')"
    :cancel-label="t('PIPELINE.CANCEL')"
  />
</template>
